import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import {
  catchError,
  concatMap,
  from,
  map,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';

import { UserStore } from '../user/user.store';
import { CompanyService } from './company.service';
import { PersonService } from './person.service';
import { WizardActions } from './wizard.actions';
import { WizardService } from './wizard.service';

@Injectable()
export class WizardEffects {
  private actions$ = inject(Actions);
  private wizardService = inject(WizardService);
  private personService = inject(PersonService);
  private companyService = inject(CompanyService);
  private userStore = inject(UserStore);

  wizard$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(WizardActions.getSteps),
      switchMap(() =>
        this.wizardService.getSteps().pipe(
          concatMap(({ res }) => {
            return from([
              WizardActions.setMaxPersonProgress({
                payload: res.meta.progress[0].value,
              }),
              WizardActions.setMaxCompanyProgress({
                payload: res.meta.progress[1] ? res.meta.progress[1].value : 0,
              }),
              WizardActions.getStepsSuccess({ payload: res.children }),
            ]);
          })
        )
      )
    )
  );

  prs1$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WizardActions.stepPrs1),
      switchMap(({ personId, payload }) =>
        this.personService.updatePerson(personId, payload).pipe(
          tap(({ res }) => this.userStore.applyPersonData(res)),
          map(() => WizardActions.stepSuccess()),
          catchError(() => of(WizardActions.stepFailure()))
        )
      )
    )
  );

  prs2$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WizardActions.stepPrs2),
      switchMap(({ personId, payload }) =>
        this.personService.updatePerson(personId, payload).pipe(
          tap(({ res }) => this.userStore.applyPersonData(res)),
          map(() => WizardActions.stepSuccess()),
          catchError(() => of(WizardActions.stepFailure()))
        )
      )
    )
  );

  cmp2048$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(WizardActions.stepCmp2048),
      switchMap(({ companyId, payload }) =>
        this.companyService.updateCompany(companyId, payload).pipe(
          tap(({ res }) => this.userStore.applyCompanyData(res)),
          map(() => WizardActions.stepSuccess()),
          catchError(() => of(WizardActions.stepFailure()))
        )
      )
    )
  );
}
